import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import {
  UsersIcon,
  ClockIcon,
  ComputerDesktopIcon,
  KeyIcon,
  ChartBarIcon,
  DocumentChartBarIcon,
  SwatchIcon,
  CalendarDaysIcon,
  SunIcon,
  GiftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '@/stores/authStore';
import { useBranding } from '@/hooks/useBranding';

const COLLAPSED_KEY = 'sidebar-collapsed';

interface SidebarProps {
  /** Called when a nav link is clicked — used by the mobile drawer to close itself. */
  onNavClick?: () => void;
  /** Desktop only: shows the collapse toggle; collapsed state shows icons only. */
  collapsible?: boolean;
}

export function Sidebar({ onNavClick, collapsible = false }: SidebarProps = {}) {
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin());
  const isAdmin = useAuthStore((s) => s.isAdmin());
  // Exact admin-only check: excludes super_admin per product requirement
  const isExactAdmin = useAuthStore((s) => s.hasRole('admin'));
  const { logoUrl, companyName } = useBranding();
  const { t } = useTranslation(['admin', 'common']);

  const [collapsed, setCollapsed] = useState(
    () => collapsible && localStorage.getItem(COLLAPSED_KEY) === '1',
  );

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      const next = !c;
      localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0');
      return next;
    });
  };

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    clsx(
      'flex items-center gap-3 py-2 rounded-md text-sm font-medium transition-colors',
      collapsed ? 'justify-center px-2' : 'px-3',
      isActive
        ? 'bg-brand text-white'
        : 'text-gray-300 hover:bg-gray-700 hover:text-white',
    );

  const Item = ({ to, icon: Icon, label, badge }: { to: string; icon: typeof UsersIcon; label: string; badge?: number }) => (
    <NavLink
      to={to}
      title={collapsed ? label : undefined}
      className={navLinkClass}
      onClick={onNavClick}
    >
      <span className="relative shrink-0">
        <Icon className="h-5 w-5" aria-hidden="true" />
        {collapsed && !!badge && (
          <span className="absolute -top-1.5 -right-1.5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-gray-900" aria-hidden="true" />
        )}
      </span>
      {!collapsed && <span className="truncate flex-1">{label}</span>}
      {!collapsed && !!badge && (
        <span className="shrink-0 min-w-[1.25rem] px-1 py-0.5 rounded-full bg-red-500 text-white text-[10px] font-semibold text-center leading-none">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </NavLink>
  );

  // Section heading collapses into a simple divider when only icons are shown.
  const SectionHeading = ({ label }: { label: string }) =>
    collapsed ? (
      <div className="pt-3 pb-1">
        <div className="mx-2 border-t border-gray-700" />
      </div>
    ) : (
      <div className="pt-3 pb-1 px-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
          {label}
        </span>
      </div>
    );

  return (
    <aside
      className={clsx(
        'relative flex flex-col shrink-0 bg-gray-900 text-white min-h-screen transition-[width] duration-200',
        collapsed ? 'w-16' : 'w-56',
      )}
    >
      {collapsible && (
        <button
          type="button"
          onClick={toggleCollapsed}
          aria-label={collapsed ? t('admin:sidebar.expand') : t('admin:sidebar.collapse')}
          title={collapsed ? t('admin:sidebar.expand') : t('admin:sidebar.collapse')}
          className="absolute -right-3 top-5 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-gray-900 border border-gray-600 text-gray-400 shadow-md hover:text-white hover:border-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-400"
        >
          {collapsed ? (
            <ChevronRightIcon className="h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <ChevronLeftIcon className="h-3.5 w-3.5" aria-hidden="true" />
          )}
        </button>
      )}
      <div
        className={clsx(
          'flex items-center h-16 border-b border-gray-700 gap-2 min-w-0',
          collapsed ? 'justify-center px-2' : 'px-4',
        )}
      >
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={`${companyName} logo`}
            className="h-8 w-8 shrink-0 object-contain rounded"
            title={collapsed ? companyName : undefined}
          />
        ) : collapsed ? (
          <span className="text-lg font-semibold" title={companyName}>
            {companyName.charAt(0)}
          </span>
        ) : null}
        {!collapsed && (
          <span className="text-lg font-semibold tracking-tight truncate">{companyName}</span>
        )}
      </div>
      <nav className="flex-1 px-2 py-4 space-y-1" aria-label={t('common:nav.mainNavigation')}>
        <Item to="/dashboard" icon={ChartBarIcon} label={t('admin:sidebar.dashboard')} />

        {isAdmin && (
          <Item to="/reports" icon={DocumentChartBarIcon} label={t('admin:sidebar.reports')} />
        )}

        {(isSuperAdmin || isExactAdmin) && (
          <Item to="/settings/time-off" icon={SunIcon} label={t('admin:sidebar.timeOff')} />
        )}

        {isSuperAdmin && (
          <Item to="/devices" icon={ComputerDesktopIcon} label={t('admin:sidebar.devices')} />
        )}

        <Item to="/entries-exits" icon={ClockIcon} label={t('admin:sidebar.entriesExits')} />

        {isSuperAdmin && (
          <Item to="/users" icon={KeyIcon} label={t('admin:sidebar.users')} />
        )}

        {isSuperAdmin && (
          <>
            <SectionHeading label={t('admin:sidebar.settings')} />
            <Item to="/settings/branding" icon={SwatchIcon} label={t('admin:sidebar.branding')} />
            <Item to="/settings/holidays" icon={GiftIcon} label={t('admin:sidebar.holidays')} />
          </>
        )}

        {isExactAdmin && (
          <>
            <SectionHeading label={t('admin:sidebar.settings')} />
            <Item to="/settings/schedule" icon={CalendarDaysIcon} label={t('admin:sidebar.workSchedule')} />
            <Item to="/settings/holidays" icon={GiftIcon} label={t('admin:sidebar.holidays')} />
          </>
        )}

        <Item to="/employees" icon={UsersIcon} label={t('admin:sidebar.employees')} />
      </nav>
    </aside>
  );
}
